/* ==========================================================================
   VELDORO Product, behaviour for the veldoro-* product page sections
   Gallery, variant picker, detail tabs, animated stats and the sticky bar.
   Accordions use <details>, so they need no JS at all.
   ========================================================================== */

(function () {
  'use strict';

  // Each section emits its own script tag, so this file can be included
  // several times on one page. Every tag executes, which would register
  // duplicate document-level listeners. Run the body once.
  if (window.__veldoroProductReady) return;
  window.__veldoroProductReady = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Swipe -------------------------------------------------------------
     Horizontal drag detection for touch devices. Ignores mostly-vertical
     movement so the page can still be scrolled from on top of the element.
     ---------------------------------------------------------------------- */

  function onSwipe(el, handler) {
    var startX = 0;
    var startY = 0;
    var tracking = false;
    var THRESHOLD = 40;

    el.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        tracking = true;
      },
      { passive: true }
    );

    el.addEventListener(
      'touchend',
      function (event) {
        if (!tracking) return;
        tracking = false;

        var touch = event.changedTouches[0];
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;

        // Only act on a clearly horizontal gesture.
        if (Math.abs(dx) < THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
        handler(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  /* ---- Gallery ----------------------------------------------------------- */

  function Gallery(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    this.index = 0;
    this.bind();
  }

  Gallery.prototype.bind = function () {
    var self = this;

    this.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { self.show(i); });
    });

    // Swiping the main image moves through the gallery on touch devices.
    var stage = this.root.querySelector('.veldoro-gallery__main');
    if (stage && this.slides.length > 1) {
      onSwipe(stage, function (direction) {
        self.show((self.index + direction + self.slides.length) % self.slides.length);
      });
    }
  };

  Gallery.prototype.show = function (index) {
    this.index = index;

    this.slides.forEach(function (slide, i) {
      slide.dataset.active = i === index ? 'true' : 'false';
    });
    this.thumbs.forEach(function (thumb, i) {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    // Only the visible slide should be playing. A hidden <video> keeps
    // running otherwise, which burns bandwidth and leaks audio.
    this.slides.forEach(function (slide, i) {
      var vid = slide.querySelector('video');
      if (!vid) return;
      if (i === index) {
        if (vid.autoplay) {
          var p = vid.play();
          if (p && p.catch) p.catch(function () { /* blocked by the browser */ });
        }
      } else if (!vid.paused) {
        vid.pause();
      }
    });

    // Keep the active thumbnail inside the scrolling strip.
    var active = this.thumbs[index];
    if (active && active.scrollIntoView) {
      active.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  };

  // Jump the gallery to the first media belonging to a variant.
  Gallery.prototype.showMediaId = function (mediaId) {
    if (!mediaId) return;
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].dataset.mediaId === String(mediaId)) {
        this.show(i);
        return;
      }
    }
  };

  /* ---- Variant picker ---------------------------------------------------- */

  function VariantPicker(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.variants = this.readVariants();
    this.form = document.querySelector('[data-veldoro-product-form="' + this.sectionId + '"]');
    this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    this.bind();
  }

  VariantPicker.prototype.readVariants = function () {
    var el = this.root.querySelector('[data-variant-json]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  };

  VariantPicker.prototype.bind = function () {
    var self = this;
    this.root.querySelectorAll('[data-variant-option]').forEach(function (input) {
      var evt = input.tagName === 'SELECT' ? 'change' : 'click';
      input.addEventListener(evt, function () {
        if (evt === 'click') {
          // Radio-style swatches: clear siblings in the same option group.
          self.root
            .querySelectorAll('[data-variant-option][data-option-index="' + input.dataset.optionIndex + '"]')
            .forEach(function (sib) { sib.setAttribute('aria-checked', 'false'); });
          input.setAttribute('aria-checked', 'true');
        }
        self.update();
      });
    });
  };

  VariantPicker.prototype.selectedOptions = function () {
    var chosen = [];
    this.root.querySelectorAll('[data-option-group]').forEach(function (group) {
      var idx = parseInt(group.dataset.optionGroup, 10);
      var picked = group.querySelector('[aria-checked="true"]');
      if (picked) {
        chosen[idx] = picked.dataset.optionValue;
      } else {
        var select = group.querySelector('select[data-variant-option]');
        if (select) chosen[idx] = select.value;
      }
    });
    return chosen;
  };

  VariantPicker.prototype.match = function (chosen) {
    return this.variants.find(function (v) {
      return chosen.every(function (value, i) { return v.options[i] === value; });
    });
  };

  VariantPicker.prototype.update = function () {
    var variant = this.match(this.selectedOptions());
    if (!variant) return;

    if (this.idInput) this.idInput.value = variant.id;

    // The sticky bar posts its own form; give it the same variant.
    document
      .querySelectorAll('form[data-veldoro-product-form-sticky="' + this.sectionId + '"] input[name="id"]')
      .forEach(function (input) { input.value = variant.id; });

    // Keep the URL in step so a refresh or a share keeps the chosen variant.
    if (history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    this.paint(variant);

    document.dispatchEvent(
      new CustomEvent('veldoro:variant:change', { detail: { variant: variant, sectionId: this.sectionId } })
    );
  };

  VariantPicker.prototype.paint = function (variant) {
    var scope = document.querySelector('[data-veldoro-product="' + this.sectionId + '"]') || document;

    var setText = function (sel, value) {
      var el = scope.querySelector(sel);
      if (el) el.innerHTML = value;
    };

    setText('[data-price-current]', variant.price);
    setText('[data-variant-value]', variant.title);

    var compare = scope.querySelector('[data-price-compare]');
    if (compare) compare.hidden = !variant.compare_at_price;
    if (compare && variant.compare_at_price) compare.innerHTML = variant.compare_at_price;

    var save = scope.querySelector('[data-price-save]');
    if (save) {
      save.hidden = !variant.saving;
      if (variant.saving) save.innerHTML = save.dataset.template.replace('[amount]', variant.saving);
    }

    var button = scope.querySelector('[data-atc-button]');
    if (button) {
      button.disabled = !variant.available;
      var label = button.querySelector('[data-atc-label]');
      if (label) {
        label.textContent = variant.available ? button.dataset.labelAvailable : button.dataset.labelSoldOut;
      }
    }

    var gallery = scope.querySelector('[data-veldoro-gallery]');
    if (gallery && gallery.veldoroGallery) gallery.veldoroGallery.showMediaId(variant.featured_media_id);

    // The sticky bar sits outside the product wrapper, so look it up directly.
    var bar = document.querySelector('[data-veldoro-stickybar][data-section-id="' + this.sectionId + '"]');
    if (bar) {
      var barPrice = bar.querySelector('[data-sticky-price]');
      if (barPrice) barPrice.innerHTML = variant.price;
      var barVariant = bar.querySelector('[data-sticky-variant]');
      if (barVariant) barVariant.textContent = variant.title;
      var barButton = bar.querySelector('[data-sticky-button]');
      if (barButton) barButton.disabled = !variant.available;
    }
  };

  /* ---- Detail tabs ------------------------------------------------------- */

  function Tabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-tab-panel]'));

    function select(index) {
      tabs.forEach(function (tab, i) {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      panels.forEach(function (panel, i) { panel.hidden = i !== index; });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i); });
      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        if (next === null) return;
        event.preventDefault();
        select(next);
        tabs[next].focus();
      });
    });

    select(0);
  }

  /* ---- Animated stats ---------------------------------------------------- */

  function countUp(el) {
    var target = parseFloat(el.dataset.value);
    if (isNaN(target)) return;

    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';
    var decimals = parseInt(el.dataset.decimals, 10) || 0;

    if (reduceMotion) {
      el.textContent = prefix + target.toFixed(decimals) + suffix;
      return;
    }

    var duration = parseInt(el.dataset.duration, 10) || 1200;
    var start = null;

    function frame(now) {
      if (start === null) start = now;
      var progress = Math.min((now - start) / duration, 1);
      // ease-out so the number settles rather than stopping dead
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (progress < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  function initStats(root) {
    var values = Array.prototype.slice.call(root.querySelectorAll('[data-stat-value]'));
    if (!values.length) return;

    if (!('IntersectionObserver' in window)) {
      values.forEach(countUp);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countUp(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    values.forEach(function (v) { observer.observe(v); });
  }

  /* ---- Sticky add-to-cart bar -------------------------------------------- */

  function StickyBar(bar) {
    var anchorId = bar.dataset.anchor;
    var anchor = anchorId ? document.querySelector(anchorId) : null;
    if (!anchor || !('IntersectionObserver' in window)) return;

    // Show the bar once the real add-to-cart button has scrolled up and out
    // of view, and hide it again over the footer so it never covers the
    // links there. Before the button is reached it stays hidden: the button
    // itself is coming, and two of them on one screen reads as pushy.
    var pastButton = false;
    var footerInView = false;

    function render() {
      var visible = pastButton && !footerInView;
      bar.dataset.visible = visible ? 'true' : 'false';
      // Off screen is not enough: keep the hidden button out of the tab
      // order and away from screen readers, or it gets announced and
      // activated twice next to the real one.
      bar.inert = !visible;
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      });
      render();
    }).observe(anchor);

    var footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (footer) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { footerInView = entry.isIntersecting; });
        render();
      }).observe(footer);
    }
  }


  /* ---- Add to cart, into the drawer -------------------------------------
     The theme owns a <cart-drawer> custom element whose renderContents()
     swaps in fresh markup and opens the drawer. Submitting the form normally
     would POST and land the customer on /cart, so intercept it, add over
     fetch, then hand the response to the drawer.

     Everything here degrades: if the drawer element or its API is missing,
     the listener bails before preventDefault() and the browser performs the
     ordinary form post exactly as before.
     ---------------------------------------------------------------------- */

  function getCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.renderContents === 'function' ? drawer : null;
  }

  function setBusy(button, busy) {
    if (!button) return;
    var label = button.querySelector('[data-atc-label]');
    if (busy) {
      button.dataset.veldoroBusy = 'true';
      button.setAttribute('aria-busy', 'true');
      if (label && !label.dataset.veldoroIdle) {
        label.dataset.veldoroIdle = label.textContent;
        if (button.dataset.labelAdding) label.textContent = button.dataset.labelAdding;
      }
    } else {
      delete button.dataset.veldoroBusy;
      button.removeAttribute('aria-busy');
      if (label && label.dataset.veldoroIdle) {
        label.textContent = label.dataset.veldoroIdle;
        delete label.dataset.veldoroIdle;
      }
    }
  }

  function showFormError(form, message) {
    var target = form.querySelector('[data-atc-error]');
    if (!target) return;
    target.textContent = message || '';
    target.hidden = !message;
  }

  function onProductSubmit(event) {
    var form = event.currentTarget;

    // The sticky form is a copy: take the variant from the main form, which
    // the picker owns, so the bar can never add a different plug.
    var stickyFor = form.dataset.veldoroProductFormSticky;
    if (stickyFor) {
      var main = document.querySelector('form[data-veldoro-product-form="' + stickyFor + '"] input[name="id"]');
      var own = form.querySelector('input[name="id"]');
      if (main && own) own.value = main.value;
    }

    var drawer = getCartDrawer();
    // No drawer on the page: let the normal post through untouched.
    if (!drawer) return;

    event.preventDefault();

    var button = form.querySelector('[data-atc-button]') || form.querySelector('[type="submit"]');
    if (button && button.dataset.veldoroBusy === 'true') return;

    showFormError(form, '');
    setBusy(button, true);

    var body = new FormData(form);
    // Ask for the drawer's own sections back in the same round trip.
    try {
      var ids = drawer.getSectionsToRender().map(function (s) { return s.id; });
      if (ids.length) {
        body.append('sections', ids.join(','));
        body.append('sections_url', window.location.pathname);
      }
    } catch (e) {
      /* older drawer build, sections are optional */
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          // 422 and friends carry a human readable reason.
          var message = result.data && (result.data.description || result.data.message);
          // The sticky bar has no room for an error line, so say it under
          // the main button and bring that into view.
          var mainForm = stickyFor
            ? document.querySelector('form[data-veldoro-product-form="' + stickyFor + '"]')
            : null;
          if (mainForm && !form.querySelector('[data-atc-error]')) {
            showFormError(mainForm, message);
            mainForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            showFormError(form, message);
          }
          return;
        }
        drawer.renderContents(result.data);
        document.dispatchEvent(new CustomEvent('cart:ajax-update', { detail: result.data }));
      })
      .catch(function () {
        // Network or parsing failure: fall back to a real submit so the
        // customer is never left with a button that silently does nothing.
        form.removeEventListener('submit', onProductSubmit);
        form.submit();
      })
      .then(function () { setBusy(button, false); });
  }


  /* ---- Video reel --------------------------------------------------------
     Autoplaying clips, but only while they are actually on screen. Decoding
     video the customer cannot see costs battery and bandwidth for nothing,
     and on mobile several simultaneous streams will stutter.

     Sound is opt in per tile. Autoplay is muted because every browser blocks
     unmuted autoplay, so unmuting is wired to a real click instead.
     ---------------------------------------------------------------------- */

  function initReel(root) {
    var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-reel-tile]'));
    if (!tiles.length) return;

    var autoplay = root.dataset.reelAutoplay !== 'false';

    tiles.forEach(function (tile) {
      var video = tile.querySelector('[data-reel-video]');
      if (!video) return;

      var sound = tile.querySelector('[data-reel-sound]');
      if (sound) {
        sound.addEventListener('click', function () {
          var on = video.muted;
          // One tile with sound at a time, otherwise the row becomes noise.
          tiles.forEach(function (other) {
            var v = other.querySelector('[data-reel-video]');
            var b = other.querySelector('[data-reel-sound]');
            if (v && v !== video) v.muted = true;
            if (b && b !== sound) b.setAttribute('aria-pressed', 'false');
          });
          video.muted = !on;
          sound.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on && video.paused) video.play().catch(function () {});
        });
      }

      var expand = tile.querySelector('[data-reel-expand]');
      if (expand) {
        expand.addEventListener('click', function () {
          var go = video.requestFullscreen || video.webkitEnterFullscreen || video.webkitRequestFullscreen;
          if (go) {
            try { go.call(video); } catch (e) { /* fullscreen refused */ }
          }
          video.muted = false;
          video.controls = true;
          if (video.paused) video.play().catch(function () {});
        });
      }
    });

    if (!autoplay || reduceMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target.querySelector('[data-reel-video]');
          if (!video) return;
          if (entry.isIntersecting) {
            // preload is "none" until we actually need the bytes
            if (video.preload === 'none') video.preload = 'auto';
            var p = video.play();
            if (p && p.catch) p.catch(function () { /* blocked, poster stays */ });
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    tiles.forEach(function (tile) { observer.observe(tile); });
  }

  /* ---- Boot -------------------------------------------------------------- */

  function init(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-veldoro-gallery]').forEach(function (el) {
      if (el.dataset.veldoroInit === 'true') return;
      el.dataset.veldoroInit = 'true';
      el.veldoroGallery = new Gallery(el);
    });

    root.querySelectorAll('[data-veldoro-variants]').forEach(function (el) {
      if (el.dataset.veldoroInit === 'true') return;
      el.dataset.veldoroInit = 'true';
      new VariantPicker(el);
    });

    root.querySelectorAll('[data-veldoro-tabs]').forEach(function (el) {
      if (el.dataset.veldoroInit === 'true') return;
      el.dataset.veldoroInit = 'true';
      Tabs(el);
    });

    root.querySelectorAll('[data-veldoro-stats]').forEach(function (el) {
      if (el.dataset.veldoroInit === 'true') return;
      el.dataset.veldoroInit = 'true';
      initStats(el);
    });

    root
      .querySelectorAll('form[data-veldoro-product-form], form[data-veldoro-product-form-sticky]')
      .forEach(function (el) {
        if (el.dataset.veldoroInit === 'true') return;
        el.dataset.veldoroInit = 'true';
        el.addEventListener('submit', onProductSubmit);
      });

    root.querySelectorAll('[data-veldoro-reel]').forEach(function (el) {
      if (el.dataset.veldoroInit === 'true') return;
      el.dataset.veldoroInit = 'true';
      initReel(el);
    });

    root.querySelectorAll('[data-veldoro-stickybar]').forEach(function (el) {
      if (el.dataset.veldoroInit === 'true') return;
      el.dataset.veldoroInit = 'true';
      StickyBar(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
